/**
 * The cartoon's running order: videos interleaved with Rive artboards, and the
 * sound that hangs off each one.
 *
 *   Video S01.01 → Rive S01.02 → Video S02.01 → Rive S02.02 → …
 *
 * A port of `buildSegments` and its helpers from the Character Editor's
 * `animationContract.ts` in blubblub/personalized-animations, which is where
 * the chain convention is authored and unit-tested. The copy under `admin/`
 * predates the chain (it still knows the master-artboard structure), so the
 * funnel carries the newer half here rather than reaching through `@editor`
 * for a version that does not have it. Change the convention THERE first; this
 * file follows it.
 *
 * Everything is wired by name. `S<major>.<minor>` anywhere in a name is the
 * running order, read identically off artboard names and file names, so the
 * two halves interleave without either side knowing about the other:
 *
 *   • a video number NO artboard has  → a link of the chain, in sequence
 *   • a video number an artboard HAS  → that artboard's background video
 *   • an audio file with no role token → the narration of that shot
 *   • `sfx` in the name                → that shot's effect
 *   • `music`                          → where a track STARTS; it then runs on
 *                                        until another music file names a new one
 *   • `amb` / `ambience`, majors only  → the bed under every shot of those scenes
 */

/** Every artboard in the .riv that is not the editing depot is a story segment. */
const EDITOR_ARTBOARD_RE = /depo/i;
export const isSceneArtboard = (name: string): boolean => !EDITOR_ARTBOARD_RE.test(name);

/** `S<major>.<minor>` anywhere in a name. */
const SEGMENT_NUMBER_RE = /(?:^|[^A-Za-z0-9])S(\d+)(?:[._-](\d+))?(?![0-9])/i;

/** Sort key (`major*1000 + minor`), or undefined for an unnumbered name. */
export function segmentOrder(name: string): number | undefined {
  const m = SEGMENT_NUMBER_RE.exec(name.trim());
  return m ? parseInt(m[1], 10) * 1000 + (m[2] ? parseInt(m[2], 10) : 0) : undefined;
}

/** The scene (major) a running number belongs to. */
export const sceneOf = (order: number): number => Math.floor(order / 1000);

export type SceneAudioRole = 'vo' | 'music' | 'sfx' | 'ambience';

const AUDIO_ROLE_RE = /(?:^|[^a-z0-9])(music|sfx|amb(?:ience)?)(?![a-z0-9])/i;

/** The channel a file plays on. No role token means the narration. */
export function audioRole(name: string): SceneAudioRole {
  const m = AUDIO_ROLE_RE.exec(name.trim());
  if (!m) return 'vo';
  const token = m[1].toLowerCase();
  if (token === 'music') return 'music';
  if (token === 'sfx') return 'sfx';
  return 'ambience';
}

/** Every scene an ambience bed covers: `Zoo-S02-S04-S05-Amb-Savanna.wav` → [2, 4, 5]. */
export function ambienceScenes(name: string): number[] {
  const re = /(?:^|[^A-Za-z0-9])S(\d+)(?:[._-](\d+))?(?![0-9])/gi;
  const scenes: number[] = [];
  for (let m = re.exec(name); m !== null; m = re.exec(name)) {
    const scene = parseInt(m[1], 10);
    if (!scenes.includes(scene)) scenes.push(scene);
  }
  return scenes;
}

/** How a bed changes: a fade-out followed by a fade-in, in seconds each. */
export const BED_FADE_SEC = 0.8;

export type SegmentKind = 'rive' | 'video';

export interface AnimationSegment {
  kind: SegmentKind;
  /** Artboard name for rive, served URL for video. */
  id: string;
  name: string;
  order: number;
  /** Authored length in seconds; 0 when not known yet. */
  durationSec: number;
  /** Where this segment starts in the full run. */
  startSec: number;
  /** rive only: the state machine to run. */
  stateMachine?: string;
  /** video only: the served URL. */
  src?: string;
  voiceOverSrc?: string;
  /** Runs on: consecutive segments carry the SAME value while one track plays. */
  musicSrc?: string;
  sfxSrc?: string;
  /** Runs on across the scene: both shots carry the same value. */
  ambienceSrc?: string;
  /** rive only: a muted, looping video behind the artboard. */
  backgroundSrc?: string;
}

export interface SceneArtboardInfo { name: string; stateMachines: string[] }
export interface SceneFileInfo { name: string; src: string }

export interface SegmentSources {
  artboards: SceneArtboardInfo[];
  videos: SceneFileInfo[];
  audio?: SceneFileInfo[];
}

/** The state machine to run on a story artboard: the named one, else whatever it has. */
const STATE_MACHINE_CANDIDATES = ['Story animation', 'Story', 'Animation', 'State Machine 1'];
export function pickStateMachine(names: string[]): string | undefined {
  const lower = names.map((n) => n.toLowerCase());
  for (const cand of STATE_MACHINE_CANDIDATES) {
    const i = lower.indexOf(cand.toLowerCase());
    if (i !== -1) return names[i];
  }
  return names[0];
}

/**
 * Build the running order from both halves, with every sound channel hung off
 * the same numbers. Pure: `durations` (by segment id) carries whatever lengths
 * are known, so the list can be rebuilt as they arrive.
 */
export function buildSegments(sources: SegmentSources, durations: Record<string, number> = {}): AnimationSegment[] {
  const { artboards, videos, audio = [] } = sources;
  const perShot: Record<'vo' | 'sfx', Map<number, SceneFileInfo>> = { vo: new Map(), sfx: new Map() };
  const ambByScene = new Map<number, SceneFileInfo>();
  for (const a of audio) {
    const order = segmentOrder(a.name);
    if (order === undefined) continue;
    const role = audioRole(a.name);
    if (role === 'music') continue;
    if (role === 'ambience') {
      for (const scene of ambienceScenes(a.name)) if (!ambByScene.has(scene)) ambByScene.set(scene, a);
      continue;
    }
    if (!perShot[role].has(order)) perShot[role].set(order, a);
  }
  const musicCues: { order: number; src: string }[] = [];
  const musicSeen = new Set<number>();
  for (const a of audio
    .filter((a) => audioRole(a.name) === 'music')
    .map((a) => ({ order: segmentOrder(a.name), name: a.name, src: a.src }))
    .filter((c): c is { order: number; name: string; src: string } => c.order !== undefined)
    .sort((x, y) => (x.order === y.order ? x.name.localeCompare(y.name) : x.order - y.order))) {
    if (musicSeen.has(a.order)) continue;
    musicSeen.add(a.order);
    musicCues.push({ order: a.order, src: a.src });
  }
  const musicAt = (order: number) => {
    let playing: string | undefined;
    for (const cue of musicCues) {
      if (cue.order > order) break;
      playing = cue.src;
    }
    return playing;
  };
  const orderOf = (name: string) => segmentOrder(name) ?? Number.MAX_SAFE_INTEGER;
  const channelsFor = (name: string) => {
    const order = orderOf(name);
    return {
      voiceOverSrc: perShot.vo.get(order)?.src,
      musicSrc: musicAt(order),
      sfxSrc: perShot.sfx.get(order)?.src,
      ambienceSrc: ambByScene.get(sceneOf(order))?.src,
    };
  };
  const storyArtboards = artboards.filter((a) => isSceneArtboard(a.name));
  const riveOrders = new Set(storyArtboards.map((a) => segmentOrder(a.name)).filter((o): o is number => o !== undefined));
  const bgByOrder = new Map<number, SceneFileInfo>();
  const chainVideos: SceneFileInfo[] = [];
  for (const v of videos) {
    const order = segmentOrder(v.name);
    if (order === undefined || !riveOrders.has(order)) {
      chainVideos.push(v);
      continue;
    }
    if (!bgByOrder.has(order)) bgByOrder.set(order, v);
  }
  const raw: Omit<AnimationSegment, 'startSec'>[] = [
    ...storyArtboards.map((a) => ({
      kind: 'rive' as const,
      id: a.name,
      name: a.name,
      order: orderOf(a.name),
      durationSec: durations[a.name] ?? 0,
      stateMachine: pickStateMachine(a.stateMachines),
      ...channelsFor(a.name),
      backgroundSrc: bgByOrder.get(orderOf(a.name))?.src,
    })),
    ...chainVideos.map((v) => ({
      kind: 'video' as const,
      id: v.src,
      name: v.name,
      order: orderOf(v.name),
      durationSec: durations[v.src] ?? 0,
      src: v.src,
      ...channelsFor(v.name),
    })),
  ];
  raw.sort((a, b) => (a.order === b.order ? a.name.localeCompare(b.name) : a.order - b.order));
  let at = 0;
  return raw.map((s) => {
    const seg = { ...s, startSec: at };
    at += s.durationSec;
    return seg;
  });
}

/**
 * Authored length of each story artboard: the timeline named after the artboard
 * (`Zoo-S02.02-Lion-animated` → timeline `Zoo-S02.02-Lion`, 288 frames at 24 fps
 * = 12 s), else any timeline carrying the same running number. Read off the
 * low-level file handle; nothing is instanced.
 */
export function readArtboardDurations(rive: unknown, names: string[]): Record<string, number> {
  interface LowLevelArtboard {
    animationCount(): number;
    animationByIndex(i: number): { name?: string; duration?: number; fps?: number } | null;
  }
  const file = (rive as { file?: { artboardByName(n: string): LowLevelArtboard | null } } | null)?.file;
  if (!file || typeof file.artboardByName !== 'function') return {};
  const out: Record<string, number> = {};
  for (const abName of names) {
    try {
      const artboard = file.artboardByName(abName);
      if (!artboard || typeof artboard.animationCount !== 'function') continue;
      const want = abName.trim().toLowerCase();
      const wantOrder = segmentOrder(abName);
      let byOrder = 0;
      for (let i = 0; i < artboard.animationCount(); i += 1) {
        const anim = artboard.animationByIndex(i);
        const name = anim?.name;
        if (!name) continue;
        const sec = (anim?.duration ?? 0) / (anim?.fps || 60);
        if (sec <= 0) continue;
        if (name.trim().toLowerCase() === want) { out[abName] = sec; byOrder = 0; break; }
        if (!byOrder && wantOrder !== undefined && segmentOrder(name) === wantOrder) byOrder = sec;
      }
      if (!out[abName] && byOrder) out[abName] = byOrder;
    } catch {
      /* disposed mid-read — leave it unknown */
    }
  }
  return out;
}
